import type { PlannerArchetype } from './planner-intent-class';
import { MONTHLY_BUSINESS_REVIEW_TEMPLATE } from './review-pack-templates/monthly-business-review';
import { QUARTERLY_BUSINESS_REVIEW_TEMPLATE } from './review-pack-templates/quarterly-business-review';
import { WEEKLY_BUSINESS_REVIEW_TEMPLATE } from './review-pack-templates/weekly-business-review';

export const REVIEW_PACK_CADENCES = [
  'weekly_business_review',
  'monthly_business_review',
  'quarterly_business_review',
  'earnings_defense',
] as const;

export type ReviewPackCadence = (typeof REVIEW_PACK_CADENCES)[number];

export const REVIEW_PACK_AUDIENCES = [
  'operating_team',
  'finance_leadership',
  'cfo_investor_relations',
  'board',
  'partner_working_session',
] as const;

export type ReviewPackAudience = (typeof REVIEW_PACK_AUDIENCES)[number];

export const REVIEW_PACK_FRESHNESS_STATUSES = [
  'fresh',
  'stale_allowed',
  'stale_blocking',
  'missing_required_source',
  'permission_blocked',
] as const;

export type ReviewPackFreshnessStatus =
  (typeof REVIEW_PACK_FRESHNESS_STATUSES)[number];

export const REVIEW_PACK_SOURCE_FAMILIES = [
  'exec_brief_v2',
  'variance_decomposition',
  'drift_realization',
  'decision_ledger_review_queue',
  'dimensional_readout',
  'trajectory_forecast',
  'earnings_defense',
] as const;

export type ReviewPackSourceFamily =
  (typeof REVIEW_PACK_SOURCE_FAMILIES)[number];

export type ReviewPackSectionTemplate = Readonly<{
  sectionId: string;
  title: string;
  purpose: string;
  required: boolean;
  sourceAnswerFamily: ReviewPackSourceFamily;
  plannerArchetype: Extract<PlannerArchetype, 'review_pack'>;
  sourceCompositionPath: string;
  requiredInputs: readonly string[];
  freshnessRequirement: Readonly<{
    maxAgeHours: number;
    staleBehavior: 'allow' | 'block';
  }>;
  proofRequirement: 'proof_ref_required';
  actionAffordances: readonly (
    | 'annotate'
    | 'adopt_into_monitoring'
    | 'share_snapshot'
  )[];
}>;

export type ReviewPackTemplate = Readonly<{
  templateId: string;
  cadence: ReviewPackCadence;
  industryProfileId: string;
  audience: ReviewPackAudience;
  sections: readonly ReviewPackSectionTemplate[];
  freshnessPolicy: Readonly<{
    generatedAtRequired: true;
    sectionAsOfRequired: true;
  }>;
  requiredPermissions: readonly string[];
  sharePolicy: 'immutable_snapshot';
  decisionLedgerCadence: 'weekly' | 'monthly' | 'quarterly';
}>;

export type ReviewPackEvidenceRef = Readonly<{
  evidenceRefId: string;
  proofRefId: string;
  sourceClass: string;
  asOf: string;
  description: string;
}>;

export type ReviewPackNumericBindingRef = Readonly<{
  bindingId: string;
  label: string;
  sourceRef: string;
  asOf: string | null;
}>;

export type ReviewPackDecisionCard = Readonly<{
  id: string;
  question: string;
  urgency: string;
  dueIn: string;
  linkedInitiativeKey: string | null;
}>;

export type ReviewPackGapInput = Readonly<{
  gapId: string;
  title: string;
  reason: string;
  owner: string;
  recoveryAction: string;
}>;

export type ReviewPackGapCard = ReviewPackGapInput &
  Readonly<{
    sectionId: string;
    sourceAnswerFamily: ReviewPackSourceFamily;
    freshnessStatus: ReviewPackFreshnessStatus;
  }>;

export type ReviewPackSourceSection = Readonly<{
  sectionId: string;
  title: string;
  sourceAnswerFamily: ReviewPackSourceFamily;
  sourceCompositionPath: string;
  asOf: string | null;
  status: 'available' | 'withheld' | 'permission_blocked';
  summary: string | null;
  body: string | null;
  evidenceRefs: readonly ReviewPackEvidenceRef[];
  numericBindings: readonly ReviewPackNumericBindingRef[];
  rows: readonly Record<string, unknown>[];
  decisions: readonly ReviewPackDecisionCard[];
  gap: ReviewPackGapInput | null;
}>;

export type ReviewPackSectionFreshness = Readonly<{
  status: ReviewPackFreshnessStatus;
  asOf: string | null;
  generatedAt: string;
  ageHours: number | null;
  maxAgeHours: number;
}>;

export type ReviewPackSection = Readonly<{
  sectionId: string;
  title: string;
  purpose: string;
  sourceAnswerFamily: ReviewPackSourceFamily;
  sourceCompositionPath: string;
  required: boolean;
  renderMode: 'source' | 'gap';
  freshness: ReviewPackSectionFreshness;
  summary: string | null;
  body: string | null;
  evidenceRefs: readonly ReviewPackEvidenceRef[];
  numericBindings: readonly ReviewPackNumericBindingRef[];
  rows: readonly Record<string, unknown>[];
  decisions: readonly ReviewPackDecisionCard[];
  gap: ReviewPackGapCard | null;
}>;

export type ReviewPack = Readonly<{
  packId: string;
  templateId: string;
  cadence: ReviewPackCadence;
  industryProfileId: string;
  audience: ReviewPackAudience;
  tenantId: string;
  scope: Readonly<{ kind: string; id: string }>;
  generatedAt: string;
  readiness: 'ready' | 'partial' | 'blocked';
  sections: readonly ReviewPackSection[];
  gaps: readonly ReviewPackGapCard[];
  evidenceRefs: readonly ReviewPackEvidenceRef[];
  numericBindings: readonly ReviewPackNumericBindingRef[];
}>;

const REVIEW_PACK_TEMPLATE_FILES = [
  WEEKLY_BUSINESS_REVIEW_TEMPLATE,
  MONTHLY_BUSINESS_REVIEW_TEMPLATE,
  QUARTERLY_BUSINESS_REVIEW_TEMPLATE,
] as const;

const REVIEW_PACK_ACTION_AFFORDANCES = [
  'annotate',
  'adopt_into_monitoring',
  'share_snapshot',
] as const;

export function loadReviewPackTemplates(): readonly ReviewPackTemplate[] {
  return REVIEW_PACK_TEMPLATE_FILES.map((template) =>
    validateReviewPackTemplate(template),
  );
}

export const REVIEW_PACK_CADENCE_TEMPLATES: readonly ReviewPackTemplate[] =
  loadReviewPackTemplates();

export function validateReviewPackTemplate(
  template: ReviewPackTemplate,
): ReviewPackTemplate {
  assertNonEmptyString(template.templateId, 'templateId');
  if (!isOneOf(template.cadence, REVIEW_PACK_CADENCES)) {
    throw new Error(
      `Review Pack template ${template.templateId} has invalid cadence`,
    );
  }
  assertNonEmptyString(template.industryProfileId, 'industryProfileId');
  if (!isOneOf(template.audience, REVIEW_PACK_AUDIENCES)) {
    throw new Error(
      `Review Pack template ${template.templateId} has invalid audience`,
    );
  }
  if (template.sections.length === 0) {
    throw new Error(
      `Review Pack template ${template.templateId} must have at least one section`,
    );
  }
  if (
    template.freshnessPolicy.generatedAtRequired !== true ||
    template.freshnessPolicy.sectionAsOfRequired !== true
  ) {
    throw new Error(
      `Review Pack template ${template.templateId} has invalid freshness policy`,
    );
  }
  if (template.sharePolicy !== 'immutable_snapshot') {
    throw new Error(
      `Review Pack template ${template.templateId} has invalid share policy`,
    );
  }
  if (
    !isOneOf(template.decisionLedgerCadence, [
      'weekly',
      'monthly',
      'quarterly',
    ])
  ) {
    throw new Error(
      `Review Pack template ${template.templateId} has invalid ledger cadence`,
    );
  }
  if (template.requiredPermissions.length === 0) {
    throw new Error(
      `Review Pack template ${template.templateId} must declare required permissions`,
    );
  }

  const sectionIds = new Set<string>();
  for (const section of template.sections) {
    assertNonEmptyString(section.sectionId, 'sectionId');
    if (sectionIds.has(section.sectionId)) {
      throw new Error(
        `Review Pack template ${template.templateId} has duplicate section ${section.sectionId}`,
      );
    }
    sectionIds.add(section.sectionId);
    assertNonEmptyString(section.title, `${section.sectionId}.title`);
    assertNonEmptyString(section.purpose, `${section.sectionId}.purpose`);
    if (!isOneOf(section.sourceAnswerFamily, REVIEW_PACK_SOURCE_FAMILIES)) {
      throw new Error(
        `Review Pack section ${section.sectionId} has invalid source family`,
      );
    }
    if (section.plannerArchetype !== 'review_pack') {
      throw new Error(
        `Review Pack section ${section.sectionId} must use review_pack archetype`,
      );
    }
    if (!/^(apps\/api|packages\/lib)\/src\//.test(section.sourceCompositionPath)) {
      throw new Error(
        `Review Pack section ${section.sectionId} has invalid source path`,
      );
    }
    if (section.requiredInputs.length === 0) {
      throw new Error(
        `Review Pack section ${section.sectionId} must declare required inputs`,
      );
    }
    if (!Number.isFinite(section.freshnessRequirement.maxAgeHours)) {
      throw new Error(
        `Review Pack section ${section.sectionId} has invalid freshness age`,
      );
    }
    if (section.freshnessRequirement.maxAgeHours <= 0) {
      throw new Error(
        `Review Pack section ${section.sectionId} has non-positive freshness age`,
      );
    }
    if (!isOneOf(section.freshnessRequirement.staleBehavior, ['allow', 'block'])) {
      throw new Error(
        `Review Pack section ${section.sectionId} has invalid stale behavior`,
      );
    }
    if (section.proofRequirement !== 'proof_ref_required') {
      throw new Error(
        `Review Pack section ${section.sectionId} must require proof refs`,
      );
    }
    for (const affordance of section.actionAffordances) {
      if (!isOneOf(affordance, REVIEW_PACK_ACTION_AFFORDANCES)) {
        throw new Error(
          `Review Pack section ${section.sectionId} has invalid action affordance`,
        );
      }
    }
  }

  return template;
}

export function getReviewPackTemplate(input: {
  cadence: ReviewPackCadence;
  industryProfileId: string;
}): ReviewPackTemplate {
  const template = REVIEW_PACK_CADENCE_TEMPLATES.find(
    (candidate) =>
      candidate.cadence === input.cadence &&
      candidate.industryProfileId === input.industryProfileId,
  );
  if (!template) {
    throw new Error(
      `No Review Pack template for ${input.cadence}/${input.industryProfileId}`,
    );
  }
  return template;
}

function assertNonEmptyString(value: string, field: string): void {
  if (value.trim().length === 0) {
    throw new Error(`Review Pack template field ${field} must be non-empty`);
  }
}

function isOneOf<T extends string>(
  value: string,
  allowed: readonly T[],
): value is T {
  return (allowed as readonly string[]).includes(value);
}
